import React, { useState } from "react";
import { SubmitHandler, useForm, get } from "react-hook-form";
import "./style/finalizeNoLoginStyle.scss";
import "../../../style/feature.scss";
import "../../../style/typography.scss";
import InvoiceButtons from "./InvoiceButtons";
import { FormElementModel } from "../../models/FormElementModel";
import { invoiceForm, userPayForm } from "./inputsForm/InputPayFormModels";
import BasketSummary from "./BasketSummary";
import { useSelector } from "react-redux";
import { State } from "../../store";
import { PaymentLogos } from "../../../features/PaymentLogo";
import Timer from "./Timer";
import { useTranslation } from "react-i18next";
import ReactPixel from "react-facebook-pixel";
import useFacility from "../../../utils/hooks/useFacility";
import RecipientAndPurchaserForm from "./RecipientAndPurchaserForm";
import { FinalizeRecipient } from "../../models/FinalizeRecipient";
import ConsentInputs from "./ConsentInputs/ConsentInputs";
import { gaEvent } from "../../../utils/Analitics/ga";
import { GA_BEGIN_CHECKOUT } from "../../../utils/Analitics/gaEventsConsts";
import { canSendToFacebookPixel, canSendToGA } from "../../../utils/Analitics/analiticsConfigByFacility";
import { sendBeginCheckoutEvent, convertBasketToBeginCheckoutFormat } from "../../../utils/Analitics/GtmEvents";
import { BasketItem } from "../../models/BasketItem";
import SubmitButton from "./SubmitButton";
import EATextField from "../shared/TextField/EATextField";
import EANipInput from "../shared/NipInput/EANipInput";
import EAPhoneInput from "../shared/PhoneInput/EAPhoneInput";
import { useCountryCodes } from "./hooks/useCountryCodes";

export type InputsFinalize = {
  firstName: string;
  lastName: string;
  email: string;
  repeatEmail: string;
  phone: string;
  city: string;
  street: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  NIP: string;
  companyName: string;
  createUser: boolean;
  createInvoice: boolean;
  acceptEAktywni: boolean;
  acceptPrivacyPolicy: boolean;
  acceptStatute: boolean;
  nipCountryCode?: string;
  recipient?: FinalizeRecipient;
  purchaser?: FinalizeRecipient;
};

interface IProps {
  onPayTransaction: (value: InputsFinalize) => void;
  onSaveReservation: (value: InputsFinalize) => void;
  alert: string;
  total: number;
  paymentUrl: string;
  loader: boolean;
  basket: BasketItem[];
  promoMessage: string;
  currentPromoCode: string;
}

const FinalizeNoLogin: React.FC<IProps> = ({
  onPayTransaction,
  onSaveReservation,
  alert,
  total,
  paymentUrl,
  loader,
  basket,
  promoMessage,
  currentPromoCode,
}: IProps) => {
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const { t } = useTranslation();

  const [invoiceType, setInvoiceType] = useState<
    "noInvoice" | "customerInvoice" | "companyInvoice" | "recipientAndPurchaserInvoice"
  >("noInvoice");
  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<InputsFinalize>({});
  const zipCodeRequired = useSelector((state: State) => state.config.configuration.zipCodeRequired);
  const phoneNumberRequired = useSelector((state: State) => state.config.configuration.phoneNumberRequired);
  const facilityIds = useFacility();
  const { countryCodes, hasError: countryCodesError } = useCountryCodes();
  const [email, setEmail] = useState<string>();
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const handleRegistration: SubmitHandler<InputsFinalize> = async (data) => {
    setEmail(data.email);

    // Czyszczenie pól faktury firmowej gdy nie wybrano faktury na firmę
    const cleanedData: InputsFinalize = {
      ...data,
      NIP: invoiceType === "companyInvoice" ? data.NIP : "",
      companyName: invoiceType === "companyInvoice" ? data.companyName : "",
      nipCountryCode: invoiceType === "companyInvoice" ? data.nipCountryCode : undefined,
    };

    // Zapisujemy stan koszyka lokalnie przed wykonaniem akcji
    const basketSnapshot = [...basket];
    const totalSnapshot = total;
    const promoCodeSnapshot = currentPromoCode;

    analiticsBeginCheckout(basketSnapshot, totalSnapshot, promoCodeSnapshot);

    // Guard to prevent multiple submissions, particularly for free orders (paymentUrl === "")
    setHasSubmitted(true);

    if (invoiceType === "recipientAndPurchaserInvoice") {
      onSaveReservation({ ...cleanedData, createInvoice: true });
    } else {
      onPayTransaction({ ...cleanedData, createInvoice: invoiceType !== "noInvoice" });
    }
    // pixelAddBasket();
  };

  const analiticsBeginCheckout = (
    basketSnapshot: any[],
    totalSnapshot: number,
    promoCodeSnapshot: string | undefined
  ) => {
    if (canSendToFacebookPixel(facilityIds)) {
      ReactPixel.fbq("track", "basketValueBeforePayU", {
        value: totalSnapshot,
        currency: "PLN",
      });
    }
    if (canSendToGA(facilityIds)) {
      gaEvent(
        facilityIds,
        "...",
        GA_BEGIN_CHECKOUT,
        {
          value: totalSnapshot,
          currency: "PLN",
        },
        ""
      );
    }

    // Wysyłanie GTM begin_checkout event
    try {
      const promoCode = promoCodeSnapshot && promoCodeSnapshot.length > 0 ? promoCodeSnapshot : undefined;
      const beginCheckoutData = convertBasketToBeginCheckoutFormat(basketSnapshot, totalSnapshot, promoCode);
      sendBeginCheckoutEvent(facilityIds, beginCheckoutData);
    } catch (error) {
      console.error("Error sending GTM begin_checkout event:", error);
    }
  };

  return (
    <div>
      <form className="finalize-no-login flex-between" onSubmit={handleSubmit(handleRegistration)}>
        <div className="basket-box shadow border">
          <h3>{t("dane_zamawiajacego")}</h3>

          {userPayForm(watch, register, zipCodeRequired).map(
            (item: FormElementModel, index: number) => {
              // When invoice is active, skip zipCode here — it's captured in the invoice form
              if (!item.name || (item.name === "zipCode" && invoiceType !== "noInvoice")) return null;
              return (
                <EATextField
                  key={index}
                  label={item.text}
                  name={item.name}
                  type={item.type as "text" | "email" | "password" | "number"}
                  placeholder={item.placeholder}
                  register={item.register}
                  error={!!get(errors, item.name)}
                  helperText={get(errors, item.name)?.message}
                  fullWidth
                  inputMaxLength={item.inputMaxLength}
                />
              );
            }
          )}

          {phoneNumberRequired && (
            <EAPhoneInput
              control={control}
              name="phone"
              errors={errors}
              label={t("numer_telefonu")}
              required
              fullWidth
            />
          )}

          {saleConfiguration.invoiceActive && (
            <>
              <h4>{t("faktura")}</h4>
              <InvoiceButtons invoiceType={invoiceType} setInvoiceType={setInvoiceType} />
            </>
          )}
        </div>

        <div className="basket-box shadow border">
          {invoiceType !== "noInvoice" && (
            <div>
              {invoiceType === "recipientAndPurchaserInvoice" ? (
                <RecipientAndPurchaserForm register={register} errors={errors} control={control} setValue={setValue} countryCodes={countryCodes} countryCodesError={countryCodesError} />
              ) : (
                <>
                  <h3>{t("dane_do_faktury")}</h3>
                  <div className={"inputs-customer-invoice"}>
                    {invoiceForm(register, invoiceType === "companyInvoice").map(
                      (item: FormElementModel, index: number) => (
                        <React.Fragment key={index}>
                          {item.name === "street" && invoiceType === "companyInvoice" && (
                            <EANipInput
                              control={control}
                              name="NIP"
                              countryCodeFieldName="nipCountryCode"
                              setValue={setValue}
                              errors={errors}
                              label={t("nip")}
                              countries={countryCodes}
                              showCountrySelect={!countryCodesError}
                              fullWidth
                            />
                          )}
                          {item.name && (
                            <EATextField
                              label={item.text}
                              name={item.name}
                              type={item.type as "text" | "email" | "password" | "number"}
                              placeholder={item.placeholder}
                              register={item.register}
                              error={!!get(errors, item.name)}
                              helperText={get(errors, item.name)?.message}
                              fullWidth
                              inputMaxLength={item.inputMaxLength}
                            />
                          )}
                        </React.Fragment>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {invoiceType === "noInvoice" && (
            <ConsentInputs
              errors={errors}
              control={control}
              setValue={setValue}
              email={email}
              title={t("zgody")}
              newsletter={true}
            />
          )}
        </div>

        <div className="basket-box shadow border">
          {invoiceType !== "noInvoice" && (
            <ConsentInputs
              errors={errors}
              control={control}
              setValue={setValue}
              email={email}
              title={t("zgody")}
              newsletter={true}
            />
          )}

          <BasketSummary total={total} />

          {alert.length > 2 && <div className="alert alert-red">{alert}</div>}

          <SubmitButton loader={loader} paymentUrl={paymentUrl} hasSubmitted={hasSubmitted} />
          <Timer />

          <PaymentLogos />
        </div>
      </form>
    </div>
  );
};

export default FinalizeNoLogin;
